import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api";
import { CURRENCY_SYMBOLS } from "../currency";


/**
 * Manage per-employee flat payouts for a single page (sheet).
 *
 * Props:
 *   open
 *   sheet         : sheet object
 *   allEmployees  : list of {id, name} on the project
 *   onClose
 *   onSaved
 */
export default function SheetPayoutsModal({
  open,
  sheet,
  allEmployees = [],
  onClose,
  onSaved,
}) {
  const [rows, setRows] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const symbol = CURRENCY_SYMBOLS[sheet?.currency] || "$";

  useEffect(() => {
    if (!open || !sheet) return;
    setErr(null);
    setBusy(false);
    // Fetch existing payouts
    api.get(`/sheets/${sheet.id}/payouts`)
      .then((r) => {
        const map = {};
        for (const p of r.data) {
          map[p.user_id] = String(p.fixed_amount);
        }
        setRows(map);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load"));
  }, [open, sheet]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose, busy]);

  if (!open || !sheet) return null;

  const setVal = (uid, v) => setRows((r) => ({ ...r, [uid]: v }));
  const clear = (uid) => setRows((r) => { const c = { ...r }; delete c[uid]; return c; });

  const handleSave = async () => {
    setBusy(true);
    setErr(null);
    try {
      const payload = Object.entries(rows)
        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
        .map(([uid, v]) => ({
          user_id: Number(uid),
          fixed_amount: Number(v),
        }));
      const { data } = await api.put(`/sheets/${sheet.id}/payouts`, payload);
      onSaved(data);
      onClose();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-backdrop" onClick={busy ? undefined : onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 520 }}
        role="dialog"
        aria-modal="true"
      >
        <h3>Milestone completion payouts</h3>
        <p className="muted" style={{ marginTop: 4, marginBottom: 16 }}>
          A flat amount paid to an employee for completing this milestone. Replaces all
          per-task math for that employee on this milestone.
        </p>

        {allEmployees.length === 0 ? (
          <div className="muted-sm">
            No employees on this project. Add employees on the project page first.
          </div>
        ) : (
          <div className="assignment-list">
            {allEmployees.map((emp) => {
              const v = rows[emp.id] ?? "";
              return (
                <div key={emp.id} className="assignment-row">
                  <span style={{ flex: 1, fontWeight: 500 }}>{emp.name}</span>
                  <div className="amount-input-wrap">
                    <span className="amount-prefix">{symbol}</span>
                    <input
                      type="number"
                      step="1"
                      placeholder="(none)"
                      value={v}
                      onChange={(e) => setVal(emp.id, e.target.value)}
                    />
                  </div>
                  {v !== "" && (
                    <button className="ghost icon" onClick={() => clear(emp.id)} title="Remove">
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {err && <div className="error" style={{ marginTop: 14 }}>{err}</div>}

        <div className="row-h" style={{ justifyContent: "flex-end", marginTop: 20, gap: 8 }}>
          <button type="button" onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" className="primary" onClick={handleSave} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
